import React, { useState, useEffect } from "react";
import { User, ChevronDown } from "lucide-react";
import ProfilePhoto from "../assets/ProfileUp";

export default function FullPageEditUser({ user, onCancel, onSave }) {
  const [formData, setFormData] = useState({
    id: user.id,
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    agentId: "",
  });

  const [roles, setRoles] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Fetch user details
  useEffect(() => {
    async function fetchUserDetails() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/${user.id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const data = await res.json();

        if (!res.ok) throw new Error("Failed to load user");

        setFormData({
          id: data.id,
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          email: data.email || "",
          role: String(data.role_id || ""),
          agentId: data.agent_id ? String(data.agent_id) : "",
        });
      } catch (err) {
        setError(err.message || "Failed to fetch user");
      } finally {
        setLoadingUser(false);
      }
    }

    fetchUserDetails();
  }, [user.id]);

  // Fetch roles
  useEffect(() => {
    async function fetchRoles() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/roles`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const data = await res.json();

        if (Array.isArray(data)) {
          setRoles(data.map((r) => ({ id: String(r.id), name: r.role })));
        }
      } catch {
        setError("Failed to load roles");
      } finally {
        setLoadingRoles(false);
      }
    }

    fetchRoles();
  }, []);

  // Fetch agents/subagents if needed
  useEffect(() => {
    const roleId = parseInt(formData.role, 10);
    if (roleId === 4 || roleId === 5) {
      setLoadingAgents(true);

      const endpoint = roleId === 4 ? "/agents" : "/subA-agent";

      fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) setAgents(data);
        })
        .catch(() => setError("Failed to load agents"))
        .finally(() => setLoadingAgents(false));
    } else {
      setAgents([]);
      setFormData((prev) => ({ ...prev, agentId: "" }));
    }
  }, [formData.role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        id: formData.id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        role_id: parseInt(formData.role, 10),
        updatedBy: user?.updatedBy || 1,
      };

      const roleId = parseInt(formData.role, 10);
      if ((roleId === 4 || roleId === 5) && formData.agentId) {
        payload.agent_id = parseInt(formData.agentId, 10);
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/update-user`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      onSave({ ...user, ...formData, role: roleId });
    } catch (err) {
      setError(err.message || "Error updating user");
    } finally {
      setSaving(false);
    }
  };

  if (loadingUser)
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Loading user information...</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="px-4 py-4">
          <h1 className="text-lg font-semibold text-gray-900">Update User</h1>
          <p className="text-sm text-gray-600 mt-1">Edit user information and permissions</p>
        </div>
      </div>

      {/* Profile Photo Section */}
      <div className="px-4 py-4 border-b border-gray-200">
        <ProfilePhoto userId={user.id} />
      </div>

      {/* Form Section */}
      <form onSubmit={handleSubmit} className="p-4 space-y-6 max-w-2xl mx-auto">
        {/* User Details Header */}
        <div className="flex items-center gap-2 text-blue-600 font-medium text-sm">
          <User className="w-4 h-4" />
          User Details
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={saving}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={saving}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={saving}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <div className="relative">
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={saving || loadingRoles}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm"
              >
                <option value="" disabled>Select Role</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {(parseInt(formData.role, 10) === 4 || parseInt(formData.role, 10) === 5) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {parseInt(formData.role, 10) === 4 ? "Select Agent" : "Select Agent / Sub-Agent"}
              </label>
              <div className="relative">
                <select
                  name="agentId"
                  value={formData.agentId}
                  onChange={handleChange}
                  disabled={saving || loadingAgents}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm"
                >
                  <option value="" disabled>
                    {loadingAgents ? "Loading agents..." : "Select"}
                  </option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name || `${agent.first_name} ${agent.last_name}`}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
          >
            {saving ? "Updating..." : "Update User"}
          </button>
        </div>
      </form>
    </div>
  );
}